import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const MongoDBCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const mongoHost = annotations['mongodb.infrastructure/host'];
  const mongoPort = annotations['mongodb.infrastructure/port'];
  const mongoDatabase = annotations['mongodb.infrastructure/database'];
  const mongoUser = annotations['mongodb.infrastructure/user'];
  const mongoPassword = annotations['mongodb.infrastructure/password'];
  const mongoVersion = annotations['mongodb.infrastructure/version'];
  const cdcEnabled = annotations['mongodb.infrastructure/cdc-enabled'];
  const replicaSet = annotations['mongodb.infrastructure/replica-set'];
  const connectionString = annotations['mongodb.infrastructure/connection-string'];
  const sshCommand = annotations['mongodb.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['mongodb.infrastructure/ssh-private-key'];

  const hasMongoDBCDCInfo = templateType === 'aws-mongodb-cdc-source' && mongoHost;
  if (!hasMongoDBCDCInfo) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'mongodb_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = mongoHost;
  const port = mongoPort || '27017';
  const database = mongoDatabase || 'rdi_source_db';
  const user = mongoUser || 'rdi_user';

  const rdiConfig = `connection:
  type: mongodb
  host: ${host}
  port: ${port}
  database: ${database}
  user: ${user}
  password: ${mongoPassword || 'RedisCDC2024!'}
  replica.set: ${replicaSet || 'rs0'}
advanced:
  source:
    connector.class: io.debezium.connector.mongodb.MongoDbConnector
    capture.mode: change_streams_update_full
    snapshot.mode: initial`;

  return (
    <InfoCard title="MongoDB CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🍃 MongoDB Configuration">
          {mongoVersion && (
            <InfraField label="Version:" value={<InfraTagValue>{`MongoDB ${mongoVersion}`}</InfraTagValue>} mono={false} />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={<InfraTagRow><InfraTag><RiCheckLine size={14} style={{ marginRight: 4 }} />Enabled</InfraTag></InfraTagRow>}
              mono={false}
            />
          )}
          {replicaSet && (
            <InfraField label="Replica Set:" value={<InfraTagValue>{replicaSet}</InfraTagValue>} mono={false} />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="User:" value={user} />
          {mongoPassword && <CopyableValue label="Password:" value={mongoPassword} />}
          {connectionString && (
            <>
              <InfraField label="Connection String:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{connectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={connectionString} />
                </div>
              </div>
            </>
          )}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button variant="secondary" size="small" onPress={() => navigator.clipboard.writeText(sshPrivateKey)}>
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as mongodb_key.pem and run: chmod 400 mongodb_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
